import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { clearSharedDate } from "@/lib/sharedDate";
import { motion } from "framer-motion";
import { Ticket, Mail, Lock, User, LogIn, UserPlus, KeyRound, ArrowLeft, ShieldCheck, Eye, EyeOff } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [showPassword, setShowPassword] = useState({
    login: false, register: false, registerConfirm: false,
    forgotNew: false, forgotConfirm: false
  });
  
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [registerForm, setRegisterForm] = useState({ name: "", storeName: "", storeAddress: "", email: "", password: "", confirmPassword: "", accessCode: "" });
  const [forgotForm, setForgotForm] = useState({ email: "", recoveryPhrase: "", newPassword: "", confirmPassword: "" });
  const [support, setSupport] = useState({ name: "Bob", phone: "859-537-5780" });

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/public/support-contact`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (!cancelled && d) setSupport({ name: d.name || "Bob", phone: d.phone || "859-537-5780" }); })
      .catch(() => { /* keep defaults */ });
    return () => { cancelled = true; };
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await login(loginForm.email, loginForm.password);
      toast.success("Welcome back!");
      clearSharedDate();
      navigate("/entry");
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error("Login error:", error);
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (registerForm.password !== registerForm.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    if (registerForm.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setLoading(true);
    
    try {
      await register(registerForm.name, registerForm.email, registerForm.password, registerForm.accessCode, { store_name: registerForm.storeName, store_address: registerForm.storeAddress });
      toast.success("Account created! An admin must activate it before you can enter data — you can look around meanwhile.", { duration: 8000 });
      navigate("/entry");
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error("Register error:", error);
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = (e) => {
    // Self-service password reset has been removed. Users now contact admin.
    // This handler is unused but kept to avoid breaking any external links.
    e?.preventDefault?.();
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center neon-glow">
            <Ticket className="w-7 h-7 text-primary" strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="font-mono text-2xl font-bold tracking-tight">Lottery Calculation</h1>
            <p className="text-xs text-muted-foreground">Terminal</p>
          </div>
        </div>

        <Card className="border-border/50 neon-glow" data-testid="auth-card">
          {showForgot ? (
            <>
              <CardHeader className="text-center">
                <CardTitle className="font-mono text-xl">Forgot Password?</CardTitle>
                <CardDescription>We&apos;ll get you back in — just contact your admin</CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 space-y-3" data-testid="forgot-contact-panel">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary/15 flex items-center justify-center flex-shrink-0">
                      <ShieldCheck className="w-4 h-4 text-primary" />
                    </div>
                    <div className="space-y-1">
                      <p className="font-mono text-sm font-bold">Contact your admin</p>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        Your admin can reset your password (and email if needed) in a few seconds — without touching any of your saved data, history, or settings.
                      </p>
                    </div>
                  </div>
                  <div className="border-t border-border/30 pt-3 space-y-2">
                    <a
                      href="tel:8595375780"
                      className="flex items-center justify-center gap-2 w-full py-2 px-3 rounded-lg bg-primary/10 hover:bg-primary/20 text-primary font-mono text-sm transition-colors"
                      data-testid="forgot-call-admin"
                    >
                      📞 Call Bob: 859-537-5780
                    </a>
                    <a
                      href="sms:8595375780?body=Hi%2C%20I%20forgot%20my%20password%20for%20Lottery%20Management.%20My%20email%3A%20"
                      className="flex items-center justify-center gap-2 w-full py-2 px-3 rounded-lg bg-secondary hover:bg-secondary/80 text-foreground font-mono text-sm transition-colors"
                      data-testid="forgot-text-admin"
                    >
                      💬 Text 859-537-5780
                    </a>
                  </div>
                  <p className="text-[10px] text-muted-foreground/80 text-center pt-1">
                    Tell them your account email — that&apos;s all they need.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => { setShowForgot(false); setForgotForm({ email: "", recoveryPhrase: "", newPassword: "", confirmPassword: "" }); }}
                  className="w-full text-center text-sm text-muted-foreground hover:text-primary transition-colors flex items-center justify-center gap-1"
                  data-testid="back-to-login"
                >
                  <ArrowLeft className="w-3 h-3" />
                  Back to Sign In
                </button>
              </CardContent>
            </>
          ) : (
            <>
              <CardHeader className="text-center">
                <CardTitle className="font-mono text-xl">Welcome</CardTitle>
                <CardDescription>Sign in to manage your lottery operations</CardDescription>
              </CardHeader>
              <CardContent>
            <Tabs defaultValue="login" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="login" className="font-mono">Sign In</TabsTrigger>
                <TabsTrigger value="register" className="font-mono">Register</TabsTrigger>
              </TabsList>
              
              {/* Login Form */}
              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="you@example.com"
                        value={loginForm.email}
                        onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                        className="pl-10"
                        required
                        data-testid="login-email"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="login-password"
                        type={showPassword.login ? "text" : "password"}
                        placeholder="••••••••"
                        value={loginForm.password}
                        onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                        className="pl-10 pr-10"
                        required
                        data-testid="login-password"
                      />
                      <button type="button" onClick={() => setShowPassword(p => ({ ...p, login: !p.login }))} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary" data-testid="login-show-password">
                        {showPassword.login ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  
                  <Button
                    type="submit"
                    className="w-full btn-primary"
                    disabled={loading}
                    data-testid="login-submit"
                  >
                    {loading ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <LogIn className="w-4 h-4 mr-2" />
                        Sign In
                      </>
                    )}
                  </Button>
                  <button
                    type="button"
                    onClick={() => setShowForgot(true)}
                    className="w-full text-center text-sm text-muted-foreground hover:text-primary transition-colors mt-2"
                    data-testid="forgot-password-link"
                  >
                    Forgot Password?
                  </button>
                </form>
              </TabsContent>
              
              {/* Register Form */}
              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="register-name">Store Owner&apos;s Name</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="register-name"
                        type="text"
                        placeholder="Owner's full name"
                        value={registerForm.name}
                        onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })}
                        className="pl-10"
                        required
                        data-testid="register-name"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="register-store-name">Store Name</Label>
                    <div className="relative">
                      <Ticket className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="register-store-name"
                        type="text"
                        placeholder="Your store name"
                        value={registerForm.storeName}
                        onChange={(e) => setRegisterForm({ ...registerForm, storeName: e.target.value })}
                        className="pl-10"
                        required
                        data-testid="register-store-name"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="register-store-address">Store Address</Label>
                    <div className="relative">
                      <Input
                        id="register-store-address"
                        type="text"
                        placeholder="123 Main St, City, State"
                        value={registerForm.storeAddress}
                        onChange={(e) => setRegisterForm({ ...registerForm, storeAddress: e.target.value })}
                        className="pl-3"
                        required
                        data-testid="register-store-address"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="register-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="register-email"
                        type="email"
                        placeholder="you@example.com"
                        value={registerForm.email}
                        onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                        className="pl-10"
                        required
                        data-testid="register-email"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="register-password">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="register-password"
                        type={showPassword.register ? "text" : "password"}
                        placeholder="••••••••"
                        value={registerForm.password}
                        onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                        className="pl-10 pr-10"
                        required
                        data-testid="register-password"
                      />
                      <button type="button" onClick={() => setShowPassword(p => ({ ...p, register: !p.register }))} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary" data-testid="register-show-password">
                        {showPassword.register ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="register-confirm">Confirm Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="register-confirm"
                        type={showPassword.registerConfirm ? "text" : "password"}
                        placeholder="••••••••"
                        value={registerForm.confirmPassword}
                        onChange={(e) => setRegisterForm({ ...registerForm, confirmPassword: e.target.value })}
                        className="pl-10 pr-10"
                        required
                        data-testid="register-confirm"
                      />
                      <button type="button" onClick={() => setShowPassword(p => ({ ...p, registerConfirm: !p.registerConfirm }))} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary" data-testid="register-confirm-show-password">
                        {showPassword.registerConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="register-access-code">Access Code</Label>

                    {/* Free promotion code call-out */}
                    <div
                      className="bg-yellow-300 border border-yellow-500 rounded-lg px-3 py-2 text-yellow-950 text-xs font-mono flex items-center gap-2"
                      data-testid="free-access-promo"
                    >
                      <span>🎉</span>
                      <span>
                        Free until end of 2026 — use code{" "}
                        <button
                          type="button"
                          onClick={() => setRegisterForm({ ...registerForm, accessCode: "ACCESS2026" })}
                          className="font-bold underline decoration-dotted hover:text-yellow-900"
                          data-testid="free-access-code-fill"
                        >
                          ACCESS2026
                        </button>
                      </span>
                    </div>

                    <div className="relative">
                      <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="register-access-code"
                        type="text"
                        placeholder="Enter access code"
                        value={registerForm.accessCode}
                        onChange={(e) => setRegisterForm({ ...registerForm, accessCode: e.target.value.toUpperCase() })}
                        className="pl-10 font-mono uppercase"
                        required
                        data-testid="register-access-code"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">Text <a href={`sms:${support.phone.replace(/\D/g,'')}`} className="text-primary font-bold hover:underline">{support.phone}</a> to reach <span className="font-bold">{support.name}</span> for your access code</p>
                  </div>
                  
                  <Button
                    type="submit"
                    className="w-full btn-primary"
                    disabled={loading}
                    data-testid="register-submit"
                  >
                    {loading ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <UserPlus className="w-4 h-4 mr-2" />
                        Create Account
                      </>
                    )}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
            </>
          )}
        </Card>
      </motion.div>
    </div>
  );
}
